import { useEffect, useMemo, useState } from "react";
import { KybComplianceDetail, KybComplianceRow } from "../../../features/kyb-compliance/types";
import { getApiErrorMessage } from "../../lib";
import { getKybBusinessDetail } from "../kyb";

type UseKybBusinessDetailState = {
  detail: KybComplianceDetail | null;
  isLoading: boolean;
  error: string | null;
};

const kybBusinessDetailCache = new Map<string, KybComplianceDetail | null>();

export const useKybBusinessDetail = (
  businessId: string,
  fallbackRow?: KybComplianceRow | null,
) => {
  const [state, setState] = useState<UseKybBusinessDetailState>({
    detail: null,
    isLoading: true,
    error: null,
  });
  const [refreshCount, setRefreshCount] = useState(0);
  const requestKey = useMemo(
    () => JSON.stringify({ businessId }),
    [businessId],
  );

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      const cached = kybBusinessDetailCache.get(requestKey) ?? null;
      setState((prev) => ({
        detail: cached ?? prev.detail,
        isLoading: cached ? false : true,
        error: null,
      }));

      try {
        const result = await getKybBusinessDetail(businessId, fallbackRow);
        if (!isMounted) return;
        kybBusinessDetailCache.set(requestKey, result.item);
        setState({
          detail: result.item,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        if (!isMounted) return;
        setState({
          detail: null,
          isLoading: false,
          error: getApiErrorMessage(error, "Unable to load business details"),
        });
      }
    };

    if (!businessId.trim()) {
      setState({
        detail: null,
        isLoading: false,
        error: "Business record not found.",
      });
      return;
    }

    run();

    return () => {
      isMounted = false;
    };
  }, [businessId, fallbackRow, refreshCount, requestKey]);

  return {
    ...state,
    refetch: () => setRefreshCount((prev) => prev + 1),
  };
};
